import { Injectable, OnDestroy } from '@angular/core';
import { HttpEventType } from '@angular/common/http';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';

import { IAuroraState } from '@apps/aurora.state';
import * as fromIts2Store from '@apps/its2/store';
import { CustomFilesList } from '@common/models/custom-files-list';
import { CustomFile } from '@common/models/custom-file';
import { RequestMiscDomainService } from '@apps/its2/domain';
import { UploadFileState } from '@common/domain/models/upload-file-state';
import { NotfGravity, NotificationService, NotifType } from '@common/services';

@Injectable({
    providedIn: 'root'
})
export class UploadFilesService implements OnDestroy
{
    get files ()
    {
        return this.customFilesList.files;
    }
    private _rfcNumber: string = '';
    get rfcNumber ()
    {
        return this._rfcNumber;
    }
    set rfcNumber (value)
    {
        this._rfcNumber = value;
    }
    private destroyator = new Subject();
    private customFilesList: CustomFilesList = new CustomFilesList();

    constructor (
        private actions: Actions,
        private store: Store<IAuroraState>,
        private requestMiscDomainService: RequestMiscDomainService,
        private notificationService: NotificationService
    )
    {
        this.actions
            .pipe(
                ofType(fromIts2Store.Its2RequestsActions.Its2RequestsFilesUploadRequested),
                switchMap((act: { rfcNumber: string }) =>
                {
                    const uploads: Observable<boolean>[] = [];

                    this.customFilesList.files.forEach((file: CustomFile) =>
                    {
                        uploads.push(this.uploadFile(act.rfcNumber, file));
                    });

                    return forkJoin(uploads);
                }),
                map((results: boolean[]) =>
                    results.reduce((acc: boolean, curr: boolean) => acc && curr, true)
                ),
                tap((result: boolean) =>
                {
                    this.store.dispatch(fromIts2Store.Its2RequestsActions.Its2RequestsFilesUploadExecuted({ result }));
                    this.clearFiles();
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    public ngOnDestroy ()
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    public processFile (file: File): void
    {
        const cf: CustomFile = file as CustomFile;

        CustomFile.addProperties(cf);
        this.customFilesList.add(cf);
        this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileAdded({ file: CustomFile.mapperToStoreCustomFile(cf) }));
    }

    public removeFile (id: string)
    {
        this.customFilesList.remove(id);
        this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileRemoved({ id }));
    }

    public clearFiles ()
    {
        this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileClearRequested());
        this.customFilesList.clear();
    }

    private uploadFile (rfcNumber: string, file: CustomFile) : Observable<boolean>
    {
        return this.requestMiscDomainService.uploadDocument(rfcNumber, file)
            .pipe(
                // tap((x: UploadFileState) =>
                //     this.swpercent = x.percent
                // ),
                filter((x: UploadFileState) =>
                    x.type === HttpEventType.Response
                ),
                map(() =>
                {
                    // this.status = 'success';
                    // this.swpercent = 100;

                    // const updatedFile: Update<CustomFile> = {
                    //     id: this.file.id,
                    //     changes: {
                    //         uploadedStatus: OperationStatus.successful
                    //     }
                    // };
                    // this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileStatusChanged({
                    //     file: updatedFile
                    // }));
                    this.notificationService.notify(
                        [
                            'ITSM.ADDACTION.FILES.NOTIF.TITLE',
                            'ITSM.ADDACTION.FILES.NOTIF.SUCCESS'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR,
                        { rfcNumber }
                    );

                    return true;
                }),
                catchError(() =>
                {
                    // this.status = 'fail';

                    // const updatedFile: Update<CustomFile> = {
                    //     id: this.file.id,
                    //     changes: {
                    //         uploadedStatus: OperationStatus.failed
                    //     }
                    // };
                    // this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileStatusChanged({
                    //     file: updatedFile
                    // }));
                    this.notificationService.notify(
                        [
                            'ITSM.ADDACTION.FILES.NOTIF.TITLE',
                            'ITSM.ADDACTION.FILES.NOTIF.FAILED'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    );

                    return of(false);
                }),
                finalize(() =>
                {
                    this.store.dispatch(fromIts2Store.Its2MiscActions.Its2RequestsFileRemoved({ id: file.id }));
                    this.customFilesList.remove(file.id);
                })
            );
    }
}
